import {
  css,
  type DefaultTheme,
  type FlattenSimpleInterpolation,
} from 'styled-components';

export type ButtonVariant =
  | 'primary'
  | 'outline'
  | 'outline-reverse'
  | 'tertiary'
  | 'tertiary-reverse'
  | 'text'
  | 'text-reverse';
type ButtonStyleProperty =
  | 'backgroundColor'
  | 'backgroundColorHover'
  | 'borderColor'
  | 'borderColorHover'
  | 'textColor'
  | 'textColorHover';

export interface ButtonStyleProps {
  /** Determines the styling applied to the button */
  variant: ButtonVariant;
  /** If the button should be rounded or not */
  rounded?: boolean;
}

interface ButtonStylePropParams extends ButtonStyleProps {
  theme: DefaultTheme;
}

const getButtonStyle = (
  { variant, theme }: ButtonStylePropParams,
  property: ButtonStyleProperty | null = null
) => {
  let styles: Record<ButtonStyleProperty, string>;

  switch (variant) {
    case 'primary':
      styles = {
        backgroundColor: theme.primaryButtonBg,
        backgroundColorHover: theme.primaryButtonBgHover,
        borderColor: theme.primaryButtonBorder,
        borderColorHover: theme.primaryButtonBorderHover,
        textColor: theme.primaryButtonText,
        textColorHover: theme.primaryButtonText,
      };
      break;
    case 'outline':
      styles = {
        backgroundColor: theme.outlineButtonBg,
        backgroundColorHover: theme.outlineButtonBgHover,
        borderColor: theme.outlineButtonBorder,
        borderColorHover: theme.outlineButtonBorderHover,
        textColor: theme.outlineButtonText,
        textColorHover: theme.outlineButtonTextHover,
      };
      break;
    case 'outline-reverse':
      styles = {
        backgroundColor: theme.outlineReverseButtonBg,
        backgroundColorHover: theme.outlineReverseButtonBgHover,
        borderColor: theme.outlineReverseButtonBorder,
        borderColorHover: theme.outlineReverseButtonBorderHover,
        textColor: theme.outlineReverseButtonText,
        textColorHover: theme.outlineReverseButtonTextHover,
      };
      break;
    case 'tertiary':
      styles = {
        backgroundColor: theme.tertiaryButtonBg,
        backgroundColorHover: theme.tertiaryButtonBgHover,
        borderColor: theme.tertiaryButtonBorder,
        borderColorHover: theme.tertiaryButtonBorderHover,
        textColor: theme.tertiaryButtonText,
        textColorHover: theme.tertiaryButtonTextHover,
      };
      break;
    case 'tertiary-reverse':
      styles = {
        backgroundColor: theme.tertiaryReverseButtonBg,
        backgroundColorHover: theme.tertiaryReverseButtonBgHover,
        borderColor: theme.tertiaryReverseButtonBorder,
        borderColorHover: theme.tertiaryReverseButtonBorderHover,
        textColor: theme.tertiaryReverseButtonText,
        textColorHover: theme.tertiaryReverseButtonTextHover,
      };
      break;
    case 'text':
      styles = {
        backgroundColor: theme.textButtonBg,
        backgroundColorHover: theme.textButtonBgHover,
        borderColor: theme.textButtonBorder,
        borderColorHover: theme.textButtonBorderHover,
        textColor: theme.textButtonText,
        textColorHover: theme.textButtonTextHover,
      };
      break;
    case 'text-reverse':
      styles = {
        backgroundColor: theme.textReverseButtonBg,
        backgroundColorHover: theme.textReverseButtonBgHover,
        borderColor: theme.textReverseButtonBorder,
        borderColorHover: theme.textReverseButtonBorderHover,
        textColor: theme.textReverseButtonText,
        textColorHover: theme.textReverseButtonTextHover,
      };
      break;
    default:
      styles = {
        backgroundColor: 'transparent',
        backgroundColorHover: 'transparent',
        borderColor: 'transparent',
        borderColorHover: 'transparent',
        textColor: 'transparent',
        textColorHover: 'transparent',
      };
  }

  return property ? styles[property] : styles;
};

export const sharedButtonStyles = (
  props: ButtonStylePropParams
): FlattenSimpleInterpolation => css`
  display: inline-flex;
  align-items: center;
  background-color: ${getButtonStyle(props, 'backgroundColor')};
  border: 1px solid ${getButtonStyle(props, 'borderColor')};
  border-radius: ${props.rounded
    ? props.theme.buttonRounded
    : props.theme.buttonRadius};
  padding: 8px 12px;
  color: ${getButtonStyle(props, 'textColor')};
  font-size: 1.2em;
  justify-content: center;
  margin: 0 auto;
  width: 100%;

  @media (any-hover: hover) {
    &:hover {
      cursor: pointer;
      background-color: ${getButtonStyle(props, 'backgroundColorHover')};
      color: ${getButtonStyle(props, 'textColorHover')};
      box-shadow: ${props.theme.buttonShadowHover};
      border: 1px solid ${getButtonStyle(props, 'borderColorHover')};
    }
  }

  & > svg {
    fill: ${getButtonStyle(props, 'textColor')};
  }

  @media (any-hover: hover) {
    &:hover > svg {
      fill: ${getButtonStyle(props, 'textColorHover')};
    }
  }
`;
